import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, Clock, MapPin } from "lucide-react";
import { UPCOMING_EVENTS } from "@/data/content";
import { Reveal } from "@/components/site/Reveal";
import { AddToCalendar } from "@/components/site/AddToCalendar";
import { SpeakerChips } from "@/components/site/SpeakerChips";
import { useEnquiry } from "@/context/EnquiryContext";
import { fmt, daysUntil, countdown, isUpcoming } from "@/lib/dates";

const calEvent = (e) => ({
  title: e.title,
  date: e.date,
  start: e.start,
  end: e.end,
  location: e.location || e.format,
  details: [`${e.topic} · ${e.lang}`, e.href ? `Register: ${e.href}` : ""].filter(Boolean).join("\n"),
});

const RegisterCta = ({ e, className, children, testId }) => {
  const { openEnquiry } = useEnquiry();
  if (e.enquiry) return <button type="button" onClick={() => openEnquiry("event", { event: e.enquiry, headcount: "1" })} className={className} data-testid={testId}>{children}</button>;
  return <a href={e.href} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId}>{children}</a>;
};

const Tag = ({ e }) => e.tag ? <span className="inline-flex items-center rounded-full border border-[rgba(212,168,77,.5)] text-[var(--gold-2)] px-2.5 py-0.5 font-mono text-[9px] tracking-[.16em] uppercase" data-testid="upcoming-tag">{e.tag}</span> : null;

export const NextSessionStrip = () => {
  const upcoming = UPCOMING_EVENTS.filter((e) => isUpcoming(e.date)).sort((a, b) => a.date.localeCompare(b.date));
  if (!upcoming.length) return null;
  const next = upcoming[0];

  return (
    <section className="section theme-dark !py-0" data-testid="home-next-session">
      <div className="af-wrap">
        <Reveal className="rounded-3xl border border-[rgba(212,168,77,.3)] bg-[linear-gradient(135deg,rgba(212,168,77,.14),rgba(255,255,255,.02))] p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-8">
          <div className="flex items-center gap-5 min-w-0 flex-1">
            <div className="shrink-0 text-center rounded-2xl border border-[rgba(212,168,77,.35)] bg-[var(--navy-2)] px-4 py-3 w-[76px]">
              <span className="block font-serif text-4xl leading-none text-[var(--gold-2)]">{fmt(next.date, { day: "2-digit" })}</span>
              <span className="block font-mono text-[9px] tracking-[.18em] uppercase text-[rgba(244,241,234,.6)] mt-1">{fmt(next.date, { month: "short" })}</span>
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gold)] text-[#071315] px-3 py-1 font-mono text-[10px] tracking-[.16em] uppercase" data-testid="home-next-countdown"><Clock size={11} /> {countdown(daysUntil(next.date))}</span>
                <span className="font-mono text-[10px] tracking-[.16em] uppercase text-[rgba(244,241,234,.5)]">Next upcoming session</span>
                <Tag e={next} />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl leading-tight mt-2.5 truncate" data-testid="home-next-title">{next.title}</h3>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-[13px] text-[rgba(244,241,234,.7)]">
                <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} className="text-[var(--gold)]" /> {fmt(next.date, { weekday: "long", day: "numeric", month: "long" })}</span>
                <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[var(--gold)]" /> {next.format}</span>
              </div>
              {next.speaker && <div className="mt-3"><SpeakerChips speaker={next.speaker} size="sm" testId="home-next-speakers" /></div>}
            </div>
          </div>
          <RegisterCta e={next} className="btn btn-gold shrink-0 self-start lg:self-auto" testId="home-next-register">Register now <ArrowUpRight size={16} /></RegisterCta>
          <AddToCalendar event={calEvent(next)} variant="ghost" dark className="shrink-0 self-start lg:self-auto" testId="home-next-calendar" />
        </Reveal>
      </div>
    </section>
  );
};

export const UpcomingEvents = () => {
  const upcoming = UPCOMING_EVENTS.filter((e) => isUpcoming(e.date)).sort((a, b) => a.date.localeCompare(b.date));
  if (!upcoming.length) return null;
  const [next, ...rest] = upcoming;

  return (
    <section className="section theme-dark relative overflow-hidden !pt-0 lg:!pt-4" id="upcoming" data-testid="upcoming-events">
      <div className="af-wrap">
        <Reveal className="rounded-3xl border border-[rgba(212,168,77,.28)] bg-[var(--navy-2)] overflow-hidden grid lg:grid-cols-[1.1fr_.9fr]">
          <div className="relative p-8 sm:p-12 border-b lg:border-b-0 lg:border-r border-white/10 overflow-hidden" data-testid="upcoming-next">
            <span aria-hidden className="absolute -right-4 -bottom-10 font-serif italic text-[clamp(120px,14vw,220px)] leading-none text-[rgba(241,214,142,.06)] select-none pointer-events-none">{fmt(next.date, { day: "2-digit" })}</span>
            <div className="relative">
              <p className="eyebrow">Next up · Register now</p>
              <div className="flex flex-wrap items-center gap-3 mt-6">
                <span className="inline-flex items-center gap-2 rounded-full bg-[var(--gold)] text-[#071315] px-3.5 py-1.5 font-mono text-[10px] tracking-[.18em] uppercase" data-testid="upcoming-next-countdown"><Clock size={12} /> {countdown(daysUntil(next.date))}</span>
                <span className="font-mono text-[10px] tracking-[.18em] uppercase text-[rgba(244,241,234,.55)]">{next.topic} · {next.lang}</span>
                <Tag e={next} />
              </div>
              <h2 className="display display-sm mt-5" data-testid="upcoming-next-title">{next.title}</h2>
              <div className="flex flex-wrap gap-x-7 gap-y-2 mt-6 text-[15px] text-[rgba(244,241,234,.75)]">
                <span className="inline-flex items-center gap-2" data-testid="upcoming-next-date"><CalendarDays size={16} className="text-[var(--gold)]" /> {fmt(next.date, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
                <span className="inline-flex items-center gap-2"><MapPin size={16} className="text-[var(--gold)]" /> {next.format}</span>
              </div>
              {next.speaker && <div className="mt-5"><p className="m-0 mb-2 font-mono text-[10px] tracking-[.18em] uppercase text-[rgba(244,241,234,.5)]">Presented by</p><SpeakerChips speaker={next.speaker} testId="upcoming-next-speakers" /></div>}
              <div className="flex flex-wrap items-center gap-4 mt-9">
                <RegisterCta e={next} className="btn btn-gold" testId="upcoming-next-register">Register for this session <ArrowUpRight size={16} /></RegisterCta>
                <AddToCalendar event={calEvent(next)} variant="ghost" dark testId="upcoming-next-calendar" />
                {next.detail && <Link to={next.detail} className="text-sm font-bold text-[var(--gold-2)] hover:underline" data-testid="upcoming-next-detail">Event details</Link>}
              </div>
            </div>
          </div>

          <div className="p-8 sm:p-12 flex flex-col" data-testid="upcoming-list">
            <p className="font-mono text-[10px] tracking-[.22em] uppercase text-[var(--gold)]">Also coming up</p>
            <ul className="m-0 p-0 list-none mt-5 flex-1">
              {rest.map((e, i) => (
                <li key={`${e.href || e.enquiry}-${e.date}`} className="grid grid-cols-[60px_1fr] sm:grid-cols-[64px_1fr_auto] items-center gap-x-5 gap-y-3 py-5 border-t border-white/10 first:border-t-0" data-testid={`upcoming-item-${i}`}>
                  <div className="text-center rounded-xl border border-white/10 py-2">
                    <span className="block font-serif text-3xl leading-none text-[var(--gold-2)]">{fmt(e.date, { day: "2-digit" })}</span>
                    <span className="block font-mono text-[9px] tracking-[.18em] uppercase text-[rgba(244,241,234,.55)] mt-1">{fmt(e.date, { month: "short" })}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2"><strong className="block font-serif text-xl leading-tight">{e.title}</strong><Tag e={e} /></div>
                    <span className="block mt-1 font-mono text-[10px] tracking-[.16em] uppercase text-[rgba(244,241,234,.5)]">{fmt(e.date, { weekday: "short" })} · {e.format} · {e.lang}</span>
                    {e.speaker && <div className="mt-2"><SpeakerChips speaker={e.speaker} size="sm" testId={`upcoming-item-speakers-${i}`} /></div>}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                      {e.detail && <Link to={e.detail} className="text-[12px] font-bold text-[var(--gold-2)] hover:underline" data-testid={`upcoming-item-detail-${i}`}>Event details</Link>}
                      <AddToCalendar event={calEvent(e)} variant="ghost" dark label="Add to calendar" className="!px-0 !py-0 !bg-transparent !border-0 !h-auto text-[12px] font-bold text-[var(--gold-2)] hover:underline" testId={`upcoming-item-calendar-${i}`} />
                    </div>
                  </div>
                  <RegisterCta e={e} className="btn btn-ghost btn-sm col-span-2 sm:col-span-1 justify-self-start sm:justify-self-auto" testId={`upcoming-item-register-${i}`}>Register <ArrowUpRight size={14} /></RegisterCta>
                </li>
              ))}
              {!rest.length && <li className="py-5 text-sm text-[rgba(244,241,234,.55)]">More sessions are being scheduled, follow us to be notified.</li>}
            </ul>
            <p className="m-0 mt-4 text-[11px] text-[rgba(244,241,234,.4)] leading-relaxed">Seats are limited. Registration opens on each programme's own page.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
